import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { Stars } from "./Stars";
import { inr } from "../lib/format";
import { toast } from "sonner";

export const SareeCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const navigate = useNavigate();
  const saved = has(product.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="group relative"
      data-testid={`saree-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div className="relative overflow-hidden rounded-lg bg-obsidian-2 gold-border aspect-[3/4]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent opacity-80" />
          <span className="absolute top-3 left-3 caption bg-obsidian/70 px-2.5 py-1 rounded">
            {product.category}
          </span>
          <button
            onClick={(e) => { e.preventDefault(); toggle(product); }}
            data-testid={`wishlist-toggle-${product.id}`}
            aria-label="Toggle wishlist"
            className="absolute top-3 right-3 w-9 h-9 grid place-items-center rounded-full bg-obsidian/70 backdrop-blur hover:bg-obsidian transition-colors"
          >
            <Heart className={`w-4 h-4 transition-colors ${saved ? "fill-gold text-gold" : "text-white/80"}`} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); navigate(`/try-on?product=${product.id}`); }}
            data-testid={`tryon-badge-${product.id}`}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 text-[11px] font-semibold bg-gold text-obsidian px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <Sparkles className="w-3 h-3" /> Try on
          </button>
        </div>
      </Link>
      <div className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <Link to={`/product/${product.id}`}>
            <h3 className="font-serif text-lg text-gold-muted leading-tight hover:text-gold transition-colors">
              {product.name}
            </h3>
          </Link>
          <span className="font-mono text-gold text-sm shrink-0" data-testid={`price-${product.id}`}>
            {inr(product.price)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{product.fabric} · {product.color}</p>
        {product.rating ? (
          <div className="flex items-center gap-2 mt-2" data-testid={`rating-${product.id}`}>
            <Stars rating={product.rating} size={13} />
            <span className="text-xs text-muted-foreground">{product.rating} ({product.review_count})</span>
          </div>
        ) : null}
        <button
          onClick={() => { addItem(product); toast.success(`${product.name} added to cart`); }}
          data-testid={`add-to-cart-${product.id}`}
          className="mt-3 w-full text-sm border border-gold/40 text-gold-muted hover:bg-gold hover:text-obsidian transition-colors duration-200 py-2 rounded-md"
        >
          Add to cart
        </button>
      </div>
    </motion.div>
  );
};
